import { useCallback, useEffect, useRef, useState } from "react";
import { twmRepository } from "../repositories/twm.repository";
import { shouldMakeAPICall } from "../utils/network";

export const useOfflineTWM = (
  authToken,
  userToken,
  page = 1,
  search = "",
  status = "all",
  matchingType = ""
) => {
  const [twmList, setTwmList] = useState([]);
  const [loadedMatchingType, setLoadedMatchingType] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(null);
  const requestIdRef = useRef(0);
  const [approversLoading, setApproversLoading] = useState(false);
  const [approversError, setApproversError] = useState(false);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;
    let basePageReceived = false;
    setLoading(true);
    setError("");
    setApproversLoading(false);
    setApproversError(false);
    const applyPage = ({ list, meta }) => {
      if (!isCurrent()) return;
      setTwmList(Array.isArray(list) ? list : []);
      setLoadedMatchingType(matchingType);
      setPaginationData(meta ? {
        ...meta,
        current_page_count: meta.total_count ?? 0,
        page_count: meta.total_pages ?? 0,
      } : null);
      setLoading(false);
    };

    const safePage = (() => {
      const n = Number(page);
      return Number.isFinite(n) && n > 0 ? n : 1;
    })();
    const safeSearch = String(search || "").trim();
    const safeStatus = String(status || "all").trim() || "all";

    if (shouldMakeAPICall(authToken, userToken)) {
      try {
        await twmRepository.syncPendingActions(authToken);
      } catch (err) {
        console.error("TWM syncPendingActions failed:", err?.message);
      }
      if (!isCurrent()) return;

      try {
        const { list, meta } = await twmRepository.fetchPageFromAPI(
          authToken,
          userToken,
          safePage,
          safeSearch,
          safeStatus,
          (basePage) => {
            if (!isCurrent()) return;
            basePageReceived = true;
            setApproversLoading(true);
            applyPage(basePage);
          },
          matchingType
        );

        applyPage({ list, meta });
        if (isCurrent()) setApproversLoading(false);
      } catch (err) {
        console.error("TWM fetchPageFromAPI failed:", err?.message);
        if (!isCurrent()) return;
        setApproversLoading(false);
        setApproversError(true);
        // Keep the fresh list if only the approval request failed.
        // Approval actions remain unavailable until a successful refresh.
        if (!basePageReceived) {
          const cached = await twmRepository.getFromCache(matchingType);
          if (!isCurrent()) return;
          setError("Unable to load matching records. Please retry.");
          setTwmList(Array.isArray(cached) ? cached : []);
          setLoadedMatchingType(matchingType);
          setPaginationData(null);
        }
      }
    } else {
      const cached = await twmRepository.getFromCache(matchingType);
      if (!isCurrent()) return;
      setTwmList(Array.isArray(cached) ? cached : []);
      setLoadedMatchingType(matchingType);
      setPaginationData(null);
    }

    if (isCurrent()) setLoading(false);
  }, [authToken, page, search, status, userToken, matchingType]);

  useEffect(() => {
    load();
    return () => { requestIdRef.current += 1; };
  }, [load]);

  const refetch = useCallback(() => load(), [load]);

  const isCurrentType = loadedMatchingType === matchingType;
  return {
    twmList: isCurrentType ? twmList : [],
    loading: loading || !isCurrentType,
    approversLoading,
    approversError,
    error,
    paginationData: isCurrentType ? paginationData : null,
    refetch,
  };
};
